import { Tooltip } from "radix-ui";
import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { OFFLINE_WRITE_REASON, useCanWrite } from "../lib/connectivity";
import type { Intent } from "./Button";
import { Spinner } from "./Spinner";

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "aria-label"> {
  /** Accessible name and tooltip text. */
  label: string;
  icon: ReactNode;
  intent?: Intent;
  emphasis?: "ghost" | "outline" | "solid";
  size?: "sm" | "md" | "lg";
  busy?: boolean;
  write?: boolean;
  pressed?: boolean;
  tooltip?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

export function IconButton({ label, icon, intent = "neutral", emphasis = "ghost", size = "md", busy, write, pressed, tooltip = true, className, disabled, onClick, type = "button", ref, ...rest }: IconButtonProps) {
  const canWrite = useCanWrite();
  const blockedOffline = !!write && !canWrite;
  const isDisabled = disabled || blockedOffline;
  const button = (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      aria-pressed={pressed}
      aria-busy={busy || undefined}
      aria-describedby={blockedOffline ? "offline-banner" : undefined}
      disabled={isDisabled}
      className={["icon-btn", `icon-btn--${emphasis}`, `icon-btn--${intent}`, `icon-btn--${size}`, className ?? ""].join(" ")}
      onClick={(event) => {
        if (busy || isDisabled) return;
        onClick?.(event);
      }}
      {...rest}
    >
      {busy ? <Spinner size="sm" label={label} /> : <span aria-hidden="true" className="icon-btn__icon">{icon}</span>}
    </button>
  );
  if (!tooltip) return button;
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content className="tooltip" sideOffset={6} collisionPadding={8}>
          {blockedOffline ? `${label} — ${OFFLINE_WRITE_REASON}` : label}
          <Tooltip.Arrow className="tooltip__arrow" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}
